import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import { AuthProvider, useAuth } from './contexts/AuthContext';

import Splash from './pages/Splash';
import Intro from './pages/Intro';
import Register from './pages/Register';
import Login from './pages/Login';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import CompleteProfile from './pages/CompleteProfile';
import SetupFamily from './pages/SetupFamily';
import CreateFamily from './pages/CreateFamily';
import JoinFamilyForm from './pages/JoinFamilyForm';
import JoinFamilyInvite from './pages/JoinFamilyInvite';
import Home from './pages/Home';
import ComingSoon from './pages/ComingSoon';
import { EmailVerified, AccountNotVerified, InvalidCode, LinkExpired } from './pages/StatusScreens';
import Family from './pages/Family';
import MemberDetail from './pages/MemberDetail';
import MemberForm from './pages/MemberForm';
import InviteMember from './pages/InviteMember';
import JoinRequests from './pages/JoinRequests';
import Notifications from './pages/Notifications';
import AcceptInvitation from './pages/AcceptInvitation';
import Listas from './pages/Listas';
import NewList from './pages/NewList';
import ListDetail from './pages/ListDetail';
import Agenda from './pages/Agenda';
import NewEvent from './pages/NewEvent';
import EventDetail from './pages/EventDetail';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminBranding, { AdminPlans, AdminLimits, AdminAds } from './pages/admin/AdminBranding';
import AdminUsers from './pages/admin/AdminUsers';
import AdminBroadcasts from './pages/admin/AdminBroadcasts';
import AdminMetrics from './pages/admin/AdminMetrics';
import AdminEvents from './pages/admin/AdminEvents';

function ProtectedAdmin({ children }) {
    const { user } = useAuth();
    if (user === null) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="w-10 h-10 border-2 border-brand/30 border-t-brand rounded-full animate-spin" />
            </div>
        );
    }
    if (user === false) return <Navigate to="/login?next=/admin" replace />;
    if (user.role !== 'admin') return <Navigate to="/home" replace />;
    return children;
}

function Protected({ children, requireFamily = false }) {
    const { user } = useAuth();
    if (user === null) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-surface">
                <div className="w-10 h-10 border-2 border-brand/30 border-t-brand rounded-full animate-spin" />
            </div>
        );
    }
    if (user === false) return <Navigate to="/intro" replace />;
    if (!user.email_verified) return <Navigate to={`/cuenta-no-verificada?email=${encodeURIComponent(user.email)}`} replace />;
    if (requireFamily && !user.family_id) return <Navigate to="/setup-family" replace />;
    return children;
}

function Public({ children }) {
    return children;
}

function AppRoutes() {
    return (
        <Routes>
            <Route path="/" element={<Splash />} />
            <Route path="/intro" element={<Public><Intro /></Public>} />
            <Route path="/registro" element={<Public><Register /></Public>} />
            <Route path="/login" element={<Public><Login /></Public>} />
            <Route path="/verificar-email" element={<VerifyEmail />} />
            <Route path="/email-verificado" element={<EmailVerified />} />
            <Route path="/cuenta-no-verificada" element={<AccountNotVerified />} />
            <Route path="/codigo-invalido" element={<InvalidCode />} />
            <Route path="/link-expirado" element={<LinkExpired />} />
            <Route path="/recuperar-contrasena" element={<ForgotPassword />} />
            <Route path="/restablecer-contrasena" element={<ResetPassword />} />
            <Route path="/unirte/:code" element={<JoinFamilyInvite />} />
            <Route path="/unirte" element={<Protected><JoinFamilyForm /></Protected>} />
            <Route path="/aceptar-invitacion" element={<AcceptInvitation />} />

            <Route path="/completar-perfil" element={<Protected><CompleteProfile /></Protected>} />
            <Route path="/setup-family" element={<Protected><SetupFamily /></Protected>} />
            <Route path="/crear-familia" element={<Protected><CreateFamily /></Protected>} />

            <Route path="/home" element={<Protected requireFamily><Home /></Protected>} />
            <Route path="/agenda" element={<Protected requireFamily><Agenda /></Protected>} />
            <Route path="/agenda/nuevo" element={<Protected requireFamily><NewEvent /></Protected>} />
            <Route path="/agenda/:eventId" element={<Protected requireFamily><EventDetail /></Protected>} />
            <Route path="/agenda/:eventId/editar" element={<Protected requireFamily><NewEvent /></Protected>} />
            <Route path="/wallet" element={<Protected requireFamily><ComingSoon title="Wallet" /></Protected>} />
            <Route path="/listas" element={<Protected requireFamily><Listas /></Protected>} />
            <Route path="/listas/nueva" element={<Protected requireFamily><NewList /></Protected>} />
            <Route path="/listas/:listId" element={<Protected requireFamily><ListDetail /></Protected>} />

            <Route path="/familia" element={<Protected requireFamily><Family /></Protected>} />
            <Route path="/familia/nuevo" element={<Protected requireFamily><MemberForm mode="create" /></Protected>} />
            <Route path="/familia/invitar" element={<Protected requireFamily><InviteMember /></Protected>} />
            <Route path="/familia/solicitudes" element={<Protected requireFamily><JoinRequests /></Protected>} />
            <Route path="/familia/miembro/:memberId" element={<Protected requireFamily><MemberDetail /></Protected>} />
            <Route path="/familia/miembro/:memberId/editar" element={<Protected requireFamily><MemberForm mode="edit" /></Protected>} />
            <Route path="/notificaciones" element={<Protected><Notifications /></Protected>} />

            <Route path="/admin" element={<ProtectedAdmin><AdminDashboard /></ProtectedAdmin>} />
            <Route path="/admin/branding" element={<ProtectedAdmin><AdminBranding /></ProtectedAdmin>} />
            <Route path="/admin/plans" element={<ProtectedAdmin><AdminPlans /></ProtectedAdmin>} />
            <Route path="/admin/limits" element={<ProtectedAdmin><AdminLimits /></ProtectedAdmin>} />
            <Route path="/admin/ads" element={<ProtectedAdmin><AdminAds /></ProtectedAdmin>} />
            <Route path="/admin/users" element={<ProtectedAdmin><AdminUsers /></ProtectedAdmin>} />
            <Route path="/admin/broadcasts" element={<ProtectedAdmin><AdminBroadcasts /></ProtectedAdmin>} />
            <Route path="/admin/metrics" element={<ProtectedAdmin><AdminMetrics /></ProtectedAdmin>} />
            <Route path="/admin/events" element={<ProtectedAdmin><AdminEvents /></ProtectedAdmin>} />

            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <BrowserRouter>
            <AuthProvider>
                <AppRoutes />
            </AuthProvider>
        </BrowserRouter>
    );
}

export default App;
